import React from "react";
import { MapPin, Waves, Sailboat, Fish, Sunset, Mountain, ExternalLink } from "lucide-react";
import { IMAGES } from "../lib/api";
import { useLanguage } from "../lib/i18n";

const MAP_URL = "https://www.google.com/maps/search/?api=1&query=Villa+Alam+Lembongan";

const ATTRACTIONS = [
    {
        icon: Waves,
        name: "Jungutbatu Beach",
        dist: "4 min walk",
        text: "Soft sand, seaweed farms, and the island's best sunsets.",
    },
    {
        icon: Mountain,
        name: "Panorama Point",
        dist: "1.7 km",
        text: "Sweeping views over Lembongan and the Bali strait.",
    },
    {
        icon: Sunset,
        name: "Devil's Tear",
        dist: "10 min scooter",
        text: "Dramatic cliff swells and golden-hour spray.",
    },
    {
        icon: Fish,
        name: "Mangrove Point",
        dist: "15 min scooter",
        text: "Glide through mangroves by kayak or traditional boat.",
    },
    {
        icon: Sailboat,
        name: "Snorkel & Dive Sites",
        dist: "Boat access",
        text: "Manta Point and Crystal Bay for world-class diving.",
    },
];

const Location = () => {
    const { t } = useLanguage();
    return (
        <div data-testid="location-page" className="pt-32 pb-24 bg-background">
            <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                <div className="overline text-secondary mb-4">
                    {t("page.location.overline")}
                </div>
                <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-light text-primary leading-[0.95] tracking-tight max-w-3xl">
                    {t("page.location.title")}
                </h1>
                <p className="mt-6 max-w-xl text-muted-foreground font-light leading-relaxed">
                    {t("page.location.intro")}
                </p>
            </div>

            <div className="mt-16 max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-10">
                <div className="lg:sticky lg:top-28 h-fit space-y-4">
                    <div className="aspect-[4/5] overflow-hidden">
                        <img
                            src={IMAGES.location[0]}
                            alt="Lembongan beach"
                            className="w-full h-full object-cover"
                        />
                    </div>
                    <div className="aspect-[16/10] overflow-hidden relative group">
                        <iframe
                            data-testid="location-map"
                            title="Villa Alam map"
                            src="https://www.google.com/maps?q=Villa+Alam+Lembongan&output=embed"
                            className="w-full h-full border-0 pointer-events-none"
                            loading="lazy"
                        />
                        <a
                            href={MAP_URL}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="open-in-maps-overlay"
                            className="absolute inset-0 flex items-end justify-center bg-gradient-to-t from-primary/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                        >
                            <span className="mb-6 inline-flex items-center gap-2 px-6 py-3 bg-white text-primary text-xs tracking-[0.2em] uppercase shadow-lg">
                                {t("cta.openInMaps")}
                                <ExternalLink size={14} />
                            </span>
                        </a>
                    </div>
                    <a
                        href={MAP_URL}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="open-in-maps-btn"
                        className="flex items-center justify-center gap-3 px-6 py-4 bg-primary text-white text-xs tracking-[0.2em] uppercase hover:bg-secondary transition-colors"
                    >
                        <MapPin size={16} strokeWidth={1.5} />
                        {t("cta.openInMaps")}
                    </a>
                </div>
                <div>
                    <div className="border-t border-border">
                        {ATTRACTIONS.map((a, i) => (
                            <div
                                key={i}
                                data-testid={`attraction-${i}`}
                                className="py-8 border-b border-border group"
                            >
                                <div className="flex items-start gap-6">
                                    <a.icon
                                        size={22}
                                        strokeWidth={1.25}
                                        className="text-secondary mt-2 shrink-0"
                                    />
                                    <div className="flex-1">
                                        <div className="flex items-baseline justify-between gap-4 flex-wrap">
                                            <h3 className="font-display text-3xl text-primary">
                                                {a.name}
                                            </h3>
                                            <div className="overline text-muted-foreground">
                                                {a.dist}
                                            </div>
                                        </div>
                                        <p className="mt-2 text-sm text-muted-foreground leading-relaxed font-light">
                                            {a.text}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="mt-10 p-8 bg-muted">
                        <div className="overline text-secondary mb-3">
                            Getting Here
                        </div>
                        <p className="text-sm text-muted-foreground leading-relaxed font-light">
                            Fly into <strong className="text-primary font-body font-medium">Ngurah Rai International (DPS)</strong> — 81 km
                            from the villa. From Sanur Port, take the fast
                            boat (≈30 min) to Jungutbatu. We'll meet you at
                            the pier.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Location;
